import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { ABOUT_FEATURES } from "@/lib/siteData";

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[#121212] grain py-20 md:py-28"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10">
        {/* Image column */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-6 relative"
        >
          <div className="relative aspect-[4/5] max-w-[520px] mx-auto lg:mx-0">
            <img
              src="/images/about-1.jpg"
              alt="Sprayed urethane foam roofing in progress"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Red frame */}
            <span className="absolute -left-3 -top-3 w-24 h-24 border-t-[3px] border-l-[3px] border-[#c41e3a]" />
            <span className="absolute -right-3 -bottom-3 w-24 h-24 border-b-[3px] border-r-[3px] border-[#c41e3a]" />

            {/* Secondary image */}
            <img
              src="/images/about-2.jpg"
              alt="Finished roof coating"
              className="hidden md:block absolute -right-10 -bottom-10 w-[230px] h-[300px] object-cover border-[6px] border-[#1a1a1a]"
            />

            {/* Badge */}
            <div className="absolute -left-4 bottom-16 md:bottom-24 bg-[#c41e3a] text-white py-4 px-5 max-w-[200px]">
              <div className="font-display font-black text-3xl leading-none">25+</div>
              <div className="font-mono-upper text-[10px] mt-2 opacity-90">
                Years serving Saskatchewan
              </div>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-[2px] bg-[#c41e3a]" />
            <span className="font-mono-upper text-[11px] text-[#c41e3a]">
              About D. Welch
            </span>
          </div>
          <h2 className="font-display font-black text-white text-4xl md:text-5xl leading-[1] tracking-[-0.02em]">
            A roof is the first line
            <span className="block text-[#c41e3a]">of defense. We build yours right.</span>
          </h2>
          <p className="font-body text-white/70 text-base md:text-lg mt-6 leading-relaxed max-w-xl">
            Based in Saskatoon, D. Welch Urethane Roofing LTD has spent decades
            protecting buildings across the prairies with sprayed polyurethane
            foam, elastomeric topcoats, and foam insulation. Our crews are
            certified, our materials are premium, and our warranties are in
            writing.
          </p>

          <ul className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {ABOUT_FEATURES.map((f, idx) => (
              <motion.li
                key={f}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: idx * 0.06 }}
                data-testid={`about-feature-${idx}`}
                className="flex items-start gap-3 font-body text-white/85 text-[15px] leading-snug"
              >
                <CheckCircle
                  size={20}
                  weight="fill"
                  className="text-[#c41e3a] shrink-0 mt-[2px]"
                />
                <span>{f}</span>
              </motion.li>
            ))}
          </ul>

          <div className="mt-10">
            <a
              href="#contact"
              data-testid="about-cta"
              className="group inline-flex items-center gap-3 bg-[#c41e3a] hover:bg-[#e62244] text-white font-bold tracking-[0.22em] uppercase text-[11px] px-6 py-4 transition-colors"
            >
              Request Free Assessment
              <ArrowRight
                size={16}
                weight="bold"
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
